import { useSelectorTyped } from 'common/store/useSelectorTyped';
import { useGetEmployeeBadgesQuery } from 'graphql.types';
import { useRef, useState } from 'react';
import RecentAchievements from './RecentAchievements';
import { feedbackText } from './helpers';
import { dateMonthYearFormat, DateTime } from 'common/utils/date';
import { Flex } from '@m/alchemy-ui/Flex';
import Stars from 'common/alchemyComponents/Stars';
import { ArrowNextIcon } from '@m/alchemy-ui/icons/ArrowNextIcon';
import * as Styled from './Styled';
import { Text } from '@m/alchemy-ui/Text';
import { StarFilledIcon } from '@m/alchemy-ui/icons/StarFilledIcon';
import { Link } from '@m/alchemy-ui/Link';
import useClickOutside from 'common/hooks/useClickOutside';
import useAchievementsEnabled from 'achievements/hooks/useAchievementsEnabled';
import { AlchemyWrapper } from 'common/alchemyComponents/AlchemyWrapper';

type Notification = {
  id: number;
  metadata: {
    stars: number;
    uuid: string;
    responded_at: string;
  };
  type: string;
  text: string;
  seen_at: string;
  dismissed_at: string;
};

const AlchemyNotifications = ({ notification }: { notification?: Notification }) => {
  const currentPerson = useSelectorTyped(state => state.currentPerson);
  const { categoriesEnabled } = useAchievementsEnabled();

  const [open, setOpen] = useState(false);
  const openNotifications = () => setOpen(true);
  const closeNotifications = () => setOpen(false);

  const openButtonId = 'OpenNotifications';

  const ref = useRef(null);
  const onClickOutside = (e: MouseEvent | TouchEvent) => {
    if ((e.target as HTMLElement)?.closest('svg')?.id === openButtonId) return;
    if (open) closeNotifications();
  };
  useClickOutside(ref, onClickOutside);

  const { data } = useGetEmployeeBadgesQuery({
    variables: {
      employeeId: currentPerson.id,
      first: 3,
    },
    skip: !categoriesEnabled || !currentPerson.id,
  });
  const badges = data?.employeeBadges?.nodes;

  if (!notification && !badges) return null;

  const notificationContents = notification && (
    <Styled.NotificationsContainer>
      <Styled.Header>Past 5-star Feedback</Styled.Header>
      <Text typeStyle="bodyS" color="typeMeta">
        {feedbackText}
      </Text>
      <>
        <Flex justifyContent="space-between">
          <Styled.Date>
            {DateTime.fromISO(notification.metadata.responded_at).toFormat(dateMonthYearFormat)}
          </Styled.Date>
          <Stars count={5} size={16} />
        </Flex>
        <div>{notification.text}</div>
        <Link
          target="_blank"
          rel="noopener noreferrer"
          href={`/requests/${notification.metadata.uuid}`}
          className="margin-left-auto"
        >
          <Styled.LinkText>
            See feedback
            <ArrowNextIcon size={14} aria-hidden className="align-self-end margin-left-small" />
          </Styled.LinkText>
        </Link>
      </>
    </Styled.NotificationsContainer>
  );

  return (
    <AlchemyWrapper>
      <Styled.OpenButton
        icon={<StarFilledIcon id={openButtonId} size={20} aria-hidden />}
        palette="expressiveWarning"
        onClick={open ? closeNotifications : openNotifications}
      />
      {open && (
        <Styled.Popup depth="card" ref={ref}>
          <>
            {notificationContents}
            {notificationContents && <Styled.Divider />}
            {categoriesEnabled && <RecentAchievements badges={badges} />}
          </>
        </Styled.Popup>
      )}
    </AlchemyWrapper>
  );
};

export default AlchemyNotifications;
